import { Big } from 'big.js';
import { add, isNaN, map, round, sum } from 'lodash';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import DateTimePicker from '../../../core/components/DateTimePicker';
import {
  checkCurrencyFormatting,
  fieldRequired,
  Input,
  requiredInput,
  requiredSelect,
} from '../../../core/formValidation';
import { formatNumber } from '../../../utils/number-util';
import PurchaseOrderItemFields from './PurchaseOrderItemFields';

const PurchaseOrderForm = (props) => {
  const totals = round(add(props.additionalCost, sum(props.itemCosts)), 2);
  const employeeListOptions = (employeeList) => {
    const options = employeeList.map((e) => ({
      value: e.id,
      text: e.value || `${e.lastName}, ${e.firstName}`,
    }));
    options.unshift({ value: null, text: '' });
    return options;
  };

  const { control } = useFormContext();

  const categoryOptions = map(props.categories, (category) => ({
    value: category.id,
    text: category.name,
  }));
  const vendorOptions = map(props.vendors, (vendor) => ({ value: vendor.id, text: vendor.name }));

  return (
    <div>
      <div className="form-group purchase-order-row po">
        <span className="text-left">
          <small htmlFor="PO" className="form-label">
            PO
          </small>
          <Controller
            name="orderNumber"
            control={control}
            render={({ field }) => (
              <Input
                input={field}
                className="form-control"
                disabled
                id="orderNumber"
                type="text"
                aria-describedby="descriptionHelp"
                placeholder="Generated on creation"
              />
            )}
          />
        </span>
        <span className="text-left">
          <small htmlFor="Order Date" className="form-label">
            Order Date
          </small>
          <Controller
            name="orderDate"
            control={control}
            render={({ field }) => (
              <DateTimePicker
                input={field}
                className="form-control"
                id="orderDate"
                showToday
                type="text"
                aria-describedby="descriptionHelp"
                required
                placeholderText={`${moment(Date.now()).format('MM/DD/YY')}`}
              />
            )}
          />
        </span>
      </div>
      <div className="form-group purchase-order-row po">
        <span className="text-left">
          <label htmlFor="categoryId">Category</label>
          <Controller
            name="categoryId"
            control={control}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: { value, onChange },
                id: 'categoryId',
                options: [{ value: null, text: '' }, ...categoryOptions],
              })
            }
          />
        </span>
        <span className="text-left">
          <label htmlFor="vendorId">Vendor</label>
          <Controller
            name="vendorId"
            control={control}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: { value, onChange },
                id: 'vendorId',
                options: [{ value: null, text: '' }, ...vendorOptions],
              })
            }
          />
        </span>
      </div>
      <div className="form-group purchase-order-row po">
        <span>
          <label htmlFor="employeeId">Team Member</label>
          <Controller
            name="employeeId"
            control={control}
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: { value, onChange },
                id: 'employeeId',
                options: employeeListOptions(props.employees),
              })
            }
          />
        </span>
        <span>
          <label htmlFor="trackingNumber">Tracking Number</label>
          <Controller
            name="trackingNumber"
            control={control}
            render={({ field }) => (
              <Input
                input={field}
                className="form-control"
                id="trackingNumber"
                type="text"
                aria-describedby="descriptionHelp"
                placeholder="Tracking Number"
              />
            )}
          />
        </span>
      </div>
      <div className="form-group">
        <label htmlFor="memo">Memo</label>
        <Controller
          name="memo"
          control={control}
          render={({ field }) => (
            <Input
              input={field}
              className="form-control"
              id="memo"
              type="text"
              aria-describedby="descriptionHelp"
              placeholder=""
            />
          )}
        />
      </div>
      <div className="form-group">
        <label htmlFor="items" className="mb-4">
          Purchase Order Items
        </label>
        <PurchaseOrderItemFields name="items" />
      </div>
      <div className="form-group purchase-order-row">
        <div>
          <small htmlFor="taxAmount" className="form-text text-muted text-left">
            Tax Amount
          </small>
          <Controller
            name="taxAmount"
            control={control}
            render={({ field }) =>
              requiredInput({
                input: field,
                className: 'form-control',
                id: 'taxAmount',
                type: 'text',
                commas: 'true',
                decimal: '2',
                placeholder: '$0.00',
                ariaDescribedBy: 'taxAmount',
              })
            }
          />
        </div>
        <div>
          <small htmlFor="shippingAmount" className="form-text text-muted text-left">
            Shipping Cost
          </small>
          <Controller
            name="shippingAmount"
            control={control}
            render={({ field }) =>
              requiredInput({
                input: field,
                className: 'form-control',
                id: 'shippingAmount',
                type: 'text',
                decimal: '2',
                commas: 'true',
                ariaDescribedBy: 'shippingCost',
                placeholder: '$0.00',
              })
            }
          />
        </div>
        <div>
          <small htmlFor="discount" className="form-text text-muted text-left">
            Discount
          </small>
          <Controller
            name="discount"
            control={control}
            render={({ field }) =>
              requiredInput({
                input: field,
                className: 'form-control',
                id: 'discount',
                type: 'text',
                decimal: '2',
                commas: 'true',
                ariaDescribedBy: 'discount',
                placeholder: '$0.00',
              })
            }
          />
        </div>
      </div>
      <hr />
      <div className="form-group form-purchase-order-totals">
        {/* eslint-disable */}
        <div>SubTotal: $ {formatNumber(Big(sum(props.itemCosts)).toFixed(2))}</div>
        <div>
          Total: ${' '}
          {isNaN(totals)
            ? '0.00'
            : formatNumber(Big(props.calculateWithDiscount(totals, props.discount)).toFixed(2))}
        </div>
      </div>
    </div>
  );
};

PurchaseOrderForm.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  vendors: PropTypes.arrayOf(PropTypes.shape({})),
  employees: PropTypes.arrayOf(PropTypes.shape({})),
  itemCosts: PropTypes.arrayOf(PropTypes.number),
  calculateWithDiscount: PropTypes.func,
  discount: PropTypes.string,
  additionalCost: PropTypes.number,
};

export default PurchaseOrderForm;
